/** @jsxImportSource @emotion/react */

import * as S from './styles';

import React, { useCallback, useEffect, useRef } from 'react';

import Portal from '../portal/portal';
import { ClickableArea } from 'ud-ui/components';
import CloseIcon from 'ud-ui/components/icon/close-icon';
import EmptyButton from 'ud-ui/components/empty-button';
import { createHotkeys } from 'shared/utils';

export type UDModalProps = {
  children: React.ReactNode | React.ReactNode[];

  show: boolean;
  onClose?: () => void;
  noOverflow?: boolean;

  disableEscapeKey?: boolean;
  disableScrollLock?: boolean;
  disableOverlayClick?: boolean;
};

const UDModal = (props: UDModalProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  createHotkeys([{ combo: ['Escape'], handler: props.onClose }]);

  // Блокировка скролла body
  useEffect(() => {
    if (!props.show || props.disableScrollLock) return;

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [props.disableScrollLock, props.show]);

  // Focus trap
  useEffect(() => {
    if (!props.show || !nodeRef.current) return;

    const modalElement = nodeRef.current;
    const focusableElements = modalElement.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    // Устанавливаем фокус на первый элемент при открытии
    firstElement?.focus();

    modalElement.addEventListener('keydown', handleTab as any);
    return () => modalElement.removeEventListener('keydown', handleTab as any);
  }, [props.show]);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (props.disableOverlayClick) return;

      // Проверяем, что клик был именно по оверлею, а не по контенту
      if (event.target === event.currentTarget && props.onClose) {
        props.onClose();
      }
    },
    [props],
  );

  if (!props.show) {
    return null;
  }

  return (
    <Portal wrapperId="portal_modal_container">
      <S.MainBlock
        className="flex items-center justify-center"
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
      >
        <S.ModalContainer className="pt-8.5 px-6 pb-10" ref={nodeRef} style={{ overflow: props.noOverflow ? 'unset' : 'auto' }}>
          <div className="close">
            <EmptyButton
              onClick={props.onClose}
              icon={
                <ClickableArea horizontalExtend={10} verticalExtend={10}>
                  <CloseIcon />
                </ClickableArea>
              }
            />
          </div>
          {props.children}
        </S.ModalContainer>
      </S.MainBlock>
    </Portal>
  );
};

export default UDModal;
